import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@components/DataTable/DataTable";

export interface SectionOption {
  id: string | number;
  name: string;
  selected: boolean;
  [key: string]: any;
}

export interface Section {
  id: string;
  name: string;
  selected: boolean;
  options: SectionOption[];
  columns: ColumnDef<SectionOption>[];
}

interface SectionTableRow {
  id: string;
  name: string;
  selectedCount: number;
  totalCount: number;
  disableSelect?: boolean;
  collapsedData?: React.ReactNode;
}

const sectionColumns: ColumnDef<SectionTableRow>[] = [
  {
    id: "name",
    header: "INCLUDE SECTIONS",
    cell: ({ row }) => (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {row.original.name}
        <span style={{ color: "#418172", fontWeight: 500, fontSize: "12px" }}>
          {row.original.selectedCount}/{row.original.totalCount} selected
        </span>
      </span>
    ),
  },
];

interface Props {
  sections: Section[];
  onSectionsChange: (sections: Section[]) => void;
}

const NestedItemSelect: React.FC<Props> = ({ sections, onSectionsChange }) => {
  const sectionData: SectionTableRow[] = sections.map((section, i) => ({
    id: section.id,
    name: section.name,
    selectedCount: section.options.filter((o) => o.selected).length,
    totalCount: section.options.length,
    disableSelect: section.options.length === 0,
    collapsedData: (
      <DataTable<SectionOption>
        data={
          section.options.length
            ? section.options
            : [
                {
                  id: "empty",
                  name: "No items in this section",
                  selected: false,
                },
              ]
        }
        columns={section.columns}
        selectable={section.options.length > 0}
        selectedIds={section.options
          .filter((o) => o.selected)
          .map((o) => String(o.id))}
        setSelectedIds={(newIds) =>
          onSectionsChange(
            sections.map((s, j) =>
              j !== i
                ? s
                : {
                    ...s,
                    selected: newIds.length > 0,
                    options: s.options.map((o) => ({
                      ...o,
                      selected: newIds.includes(String(o.id)),
                    })),
                  }
            )
          )
        }
        striped
        bordered
      />
    ),
  }));

  return (
    <DataTable<SectionTableRow>
      data={sectionData}
      columns={sectionColumns}
      isCollapsible
      selectable
      selectedIds={sections
        .filter((s) => s.selected && s.options.length > 0)
        .map((s) => s.id)}
      setSelectedIds={(newIds) =>
        onSectionsChange(
          sections.map((s) => {
            const selected = newIds.includes(s.id);
            if (selected === s.selected) return s;
            return {
              ...s,
              selected,
              options: s.options.map((o) => ({ ...o, selected })),
            };
          })
        )
      }
      striped
      bordered
    />
  );
};

export default NestedItemSelect;
